import type { ModalComponentProps } from '@qse/constants'
import { detectCurrentApp, getAdminUrl, getDashboardUrl } from '@qse/utils'
import { AlertTriangle } from 'lucide-react'
import { useEffect, useState } from 'react'

interface AccessDeniedModalData {
  userRole: string
  currentApp: string
  targetApp: string
  reason?: 'access_denied' | 'incomplete_registration'
}

export default function AccessDeniedModal(props: ModalComponentProps) {
  const { data } = props
  const [countdown, setCountdown] = useState(10)

  const modalData = data as AccessDeniedModalData
  const appDetection = detectCurrentApp()

  // Use the same pattern as sidebar - determine redirect URL based on user role and current app
  const getRedirectUrl = (): string => {
    const { userRole, reason } = modalData

    // Handle incomplete registration - redirect to admin app billing
    if (reason === 'incomplete_registration' && userRole === 'TENANT_ADMIN') {
      return getAdminUrl('/billing')
    }

    if ((userRole === 'SUPER_ADMIN' || userRole === 'RESELLER_ADMIN') && appDetection.isDashboard) {
      // Super admin in dashboard should go to admin app
      return getAdminUrl('/auth/login')
    } else if (userRole === 'TENANT_USER' && appDetection.isAdmin) {
      // Tenant user in admin should go to dashboard app
      return getDashboardUrl('/auth/login')
    }

    // Default fallback - redirect to dashboard
    return getDashboardUrl('/auth/login')
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Redirect when countdown reaches 0
          window.location.href = getRedirectUrl()

          return 0
        }

        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [modalData])

  const getMessage = () => {
    const { userRole, reason } = modalData

    // Handle incomplete registration message
    if (reason === 'incomplete_registration' && userRole === 'TENANT_ADMIN') {
      return `You need to complete your registration and set up billing before accessing the dashboard. You will be redirected to complete the registration process in ${countdown} seconds.`
    }

    if ((userRole === 'SUPER_ADMIN' || userRole === 'RESELLER_ADMIN') && appDetection.isDashboard) {
      return `You don't have permission to access this app. You will be redirected to the admin app in ${countdown} seconds.`
    } else if (userRole === 'TENANT_USER' && appDetection.isAdmin) {
      return `You don't have permission to access this app. You will be redirected to the dashboard app in ${countdown} seconds.`
    }

    return `You don't have permission to access this app. You will be redirected in ${countdown} seconds.`
  }

  return (
    <div>
      <div className="relative z-10 flex flex-col justify-center items-center">
        <div className="w-[390px] my-11 flex flex-col justify-center items-center text-center gap-5">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-white" />
          </div>

          <h1 className="font-medium text-xl text-red-600">Access Denied</h1>

          <p className="text-gray-300 leading-relaxed">{getMessage()}</p>
        </div>
      </div>
    </div>
  )
}
